/**
 * COPYRIGHT NOTICE
 * This file is categorized as "Custom Source Code"
 * and is subject to the terms and conditions defined in the
 * "LICENSE.txt" file, which is part of this source code package.
 */
import { useEffect, useState, } from 'react';
import { Platform } from 'react-native';
import {
	Box,
	Column,
	Icon,
	IconButton,
	Row,
	ScrollView,
	Text,
} from 'native-base';
import Animated, {
	Easing,
	useSharedValue,
	useAnimatedStyle,
	withDelay,
	withTiming,
	withRepeat,
} from 'react-native-reanimated';
import { decode } from 'html-entities';
import { useDimensions } from '@react-native-community/hooks';
import getIsMobile from '../OneHat/functions/getIsMobile';
import withRedux from '../OneHat/data/hoc/withRedux';
import WscInMeLogo from '../components/WscInMeLogo';
import _ from 'lodash';

const TIMING_OPTIONS = {
	duration: 500,
};

function QuestionInfo(props) {
	const {
			currentQuestion,
			audioIsLyricsMode,
			audioIsLogoVisible,
			audioQuestion,
			audioLyrics,
			audioError,
		} = props,
		isMobile = getIsMobile(),
		screen = useDimensions().screen,
		[containerHeight, setContainerHeight] = useState(0),
		[containerWidth, setContainerWidth] = useState(0),
		[whiteboardHeight, setWhiteboardHeight] = useState(0),
		[whiteboardWidth, setWhiteboardWidth] = useState(0),
		logoDimension = Math.min(whiteboardHeight, whiteboardWidth) * 0.8,

		[currentSlide, setCurrentSlide] = useState(),
		[lyricsText1, setSlideText1] = useState(''),
		[lyricsText2, setSlideText2] = useState(''),
		[lyricsText3, setSlideText3] = useState(''),
		[questionText1, setQuestionText1] = useState(''),
		[questionText2, setQuestionText2] = useState(''),
		[questionText3, setQuestionText3] = useState(''),

		lyricsOpacity1 = useSharedValue(0),
		lyricsOpacity2 = useSharedValue(0),
		lyricsOpacity3 = useSharedValue(0),
		questionOpacity1 = useSharedValue(0),
		questionOpacity2 = useSharedValue(0),
		questionOpacity3 = useSharedValue(0),
		logoOpacity = useSharedValue(0),
		logoRotation = useSharedValue(0),
 
		absolute = {
			position: 'absolute',
			w: '100%',
			h: '100%',
			zIndex: 10,
			...Platform.select({
				web: {
					top: 0,
					left: 0,
				},
			}),
			justifyContent: 'center',
			alignItems: 'center',
		},
		lyricsProps = {
			fontSize: screen.width > 600 ? 60 : 40,
			lineHeight: screen.width > 600 ? 70 : 50,
			...Platform.select({
				web: {
					display: 'inline-block',
					textAlignVertical: 'middle',
					fontSize: screen.width > 600 ? 40 : 25,
					lineHeight: screen.width > 600 ? 50 : 30,
				},
			}),
			textAlign: 'center',
			px: 7,
		},
		questionProps = {
			...lyricsProps,
			fontFamily: 'Roboto_900Black',
			fontSize: screen.width > 600 ? 80 : 50,
			lineHeight: screen.width > 600 ? 70 : 50,
			...Platform.select({
				web: {
					fontSize: screen.width > 600 ? 50 : 30,
					lineHeight: screen.width > 600 ? 60 : 40,
				},
			}),
		},
		whiteboardDimensions = {
			height: whiteboardHeight,
			width: whiteboardWidth,
		},
		lyricsStyle1 = useAnimatedStyle(() => {
			return {
				...absolute,
				...whiteboardDimensions,
				opacity: lyricsOpacity1.value,
			};
		}),
		lyricsStyle2 = useAnimatedStyle(() => {
			return {
				...absolute,
				...whiteboardDimensions,
				opacity: lyricsOpacity2.value,
			};
		}),
		lyricsStyle3 = useAnimatedStyle(() => {
			return {
				...absolute,
				...whiteboardDimensions,
				opacity: lyricsOpacity3.value,
			};
		}),
		questionStyle1 = useAnimatedStyle(() => {
			return {
				...absolute,
				...whiteboardDimensions,
				opacity: questionOpacity1.value,
			};
		}),
		questionStyle2 = useAnimatedStyle(() => {
			return {
				...absolute,
				...whiteboardDimensions,
				opacity: questionOpacity2.value,
			};
		}),
		questionStyle3 = useAnimatedStyle(() => {
			return {
				...absolute,
				...whiteboardDimensions,
				opacity: questionOpacity3.value,
			};
		}),
		logoStyle = useAnimatedStyle(() => {
			const left = (containerWidth / 2) - (logoDimension / 2),
				top = (containerHeight / 2) - (logoDimension / 2);
			return {
				...absolute,
				left,
				top,
				opacity: logoOpacity.value,
				zIndex: 0,
				transform: [
					{ rotateZ: `${logoRotation.value}deg`, }
				],
			};
		});
	
	useEffect(() => {
		// This effect switches the old/new lyrics and the flipper
		// Alternating which component fades out and fades in


		// Add optional wordbreak charaters
		let replacedLyrics = audioLyrics ? audioLyrics : '';
		if (!_.isEmpty(replacedLyrics)) {
			replacedLyrics = replacedLyrics.replace('commandment', '&#13;command&#8203;ment');
			// replacedLyrics = replacedLyrics.replace('Commandment', '&#13;Command&#8203;ment');
			replacedLyrics = decode(replacedLyrics);
		}
		
		let replacedQuestion = audioQuestion ? audioQuestion : '';
		if (!_.isEmpty(replacedQuestion)) {
			replacedQuestion = replacedQuestion.replace('commandment', '&#13;command&#8203;ment');
			replacedQuestion = replacedQuestion.replace('Commandment', '&#13;Command&#8203;ment');
			replacedQuestion = decode(replacedQuestion);
		}

		const showLogo = _.isEmpty(replacedLyrics) && _.isEmpty(replacedQuestion);
		logoOpacity.value = showLogo ? 
							withDelay(500, withTiming(1, { duration: TIMING_OPTIONS.duration * 2 })) :
							withTiming(0, { duration: TIMING_OPTIONS.duration / 2 });

		switch(currentSlide) {
			case 1:
				setCurrentSlide(2);
				setSlideText2(replacedLyrics);
				setQuestionText3(replacedQuestion);
				lyricsOpacity1.value = withTiming(0, TIMING_OPTIONS);
				lyricsOpacity2.value = withTiming(1, TIMING_OPTIONS);
				lyricsOpacity3.value = withTiming(0, TIMING_OPTIONS);
				questionOpacity1.value = withTiming(0, TIMING_OPTIONS);
				questionOpacity2.value = withTiming(0, TIMING_OPTIONS);
				questionOpacity3.value = withTiming(1, TIMING_OPTIONS);
				break;
			case 2:
				setCurrentSlide(3);
				setSlideText3(replacedLyrics);
				setQuestionText1(replacedQuestion);
				lyricsOpacity1.value = withTiming(0, TIMING_OPTIONS);
				lyricsOpacity2.value = withTiming(0, TIMING_OPTIONS);
				lyricsOpacity3.value = withTiming(1, TIMING_OPTIONS);
				questionOpacity1.value = withTiming(1, TIMING_OPTIONS);
				questionOpacity2.value = withTiming(0, TIMING_OPTIONS);
				questionOpacity3.value = withTiming(0, TIMING_OPTIONS);
				break;
			case 3:
			default:
				setCurrentSlide(1);
				setSlideText1(replacedLyrics);
				setQuestionText2(replacedQuestion);
				lyricsOpacity1.value = withTiming(1, TIMING_OPTIONS);
				lyricsOpacity2.value = withTiming(0, TIMING_OPTIONS);
				lyricsOpacity3.value = withTiming(0, TIMING_OPTIONS);
				questionOpacity1.value = withTiming(0, TIMING_OPTIONS);
				questionOpacity2.value = withTiming(1, TIMING_OPTIONS);
				questionOpacity3.value = withTiming(0, TIMING_OPTIONS);
				break;
		}
	}, [audioLyrics, audioQuestion]);

	useEffect(() => {
		logoRotation.value = withRepeat(withTiming(
			360, // deg
			{
				duration: 30 * 1000, // ms
				easing: Easing.linear(Easing.ease),
			}
		), -1); // repeat forever
	}, []);

	if (currentQuestion && currentQuestion.questions__q && currentQuestion.questions__q.length > 30) {
		// Long question, make it smaller
		questionProps.fontSize = screen.width > 600 ? 60 : 40;
		questionProps.lineHeight = screen.width > 600 ? 60 : 40;
	}

	const alignSelfCentered = {};
	if (isMobile) {
		// react-native-web doesn't allow us to assign alignSelf to SVGs, so do it only for iOS and Android
		alignSelfCentered.alignSelf = 'centered';
	}

	return <Column w="100%" flex={1} onLayout={(e) => {
				setContainerHeight(e.nativeEvent.layout.height);
				setContainerWidth(e.nativeEvent.layout.width);
			}}>
				{audioError && <Text color="#f00" fontSize={20}>{audioError.message}</Text>}
				{audioIsLyricsMode && 
					<Column w="100%" flex={1} bg="#eee" position="relative" onLayout={(e) => {
						const layout = e.nativeEvent.layout;
						setWhiteboardHeight(layout.height);
						setWhiteboardWidth(layout.width);
					}}>
						<Animated.View style={lyricsStyle1}>
							<Box {...absolute}>
								<Text {...lyricsProps}>{lyricsText1}</Text>
							</Box>
						</Animated.View>

						<Animated.View style={lyricsStyle2}>
							<Box {...absolute}>
								<Text {...lyricsProps}>{lyricsText2}</Text>
							</Box>
						</Animated.View>
						
						<Animated.View style={lyricsStyle3}>
							<Box {...absolute}>
								<Text {...lyricsProps}>{lyricsText3}</Text>
							</Box>
						</Animated.View>

						<Animated.View style={questionStyle1}>
							<Box {...absolute}>
								<Text {...questionProps}>{questionText1}</Text>
							</Box>
						</Animated.View>

						<Animated.View style={questionStyle2}>
							<Box {...absolute}>
								<Text {...questionProps}>{questionText2}</Text>
							</Box>
						</Animated.View>
						
						<Animated.View style={questionStyle3}>
							<Box {...absolute}>
								<Text {...questionProps}>{questionText3}</Text>
							</Box>
						</Animated.View>
						
						<Animated.View style={logoStyle}>
							<Box h={logoDimension} w={logoDimension} justifyContent="center" alignItems="center">
								<WscInMeLogo 
									width={logoDimension}
									height={logoDimension}
									{...alignSelfCentered}
								/>
							</Box>
						</Animated.View>
					</Column>}
				{!audioIsLyricsMode && 
						<ScrollView
							flex={1}
							w="100%"
							bg="#eee"
							contentContainerStyle={{
								justifyContent: 'center',
								alignItems: 'center',
							}}
							py={5}
						>
							<Text {...lyricsProps} fontSize={screen.width > 600 ? 35 : 28} lineHeight={screen.width > 600 ? 45 : 35}>{currentQuestion.questions__a}</Text>
						</ScrollView>}
			
			</Column>;
}

export default withRedux(QuestionInfo, [
	'currentQuestion',
	'audioIsLyricsMode',
	'audioIsLogoVisible',
	'audioQuestion',
	'audioLyrics',
	'audioError',
]);